import React from "react";
import { Link } from "react-router-dom";

/**
 * Lightweight markdown renderer tuned for wiki articles.
 * Handles: h1/h2/h3, paragraphs, bullet + ordered lists, tables (GFM),
 * inline bold/italic/code, inline links, and strips HTML comments.
 *
 * Wiki authors keep markdown deliberately simple — if we ever need
 * fenced code blocks, footnotes, etc. we'll switch to react-markdown.
 */
export default function MarkdownRenderer({ content }) {
  const blocks = parseMarkdown(content);
  return (
    <div className="space-y-1">
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  );
}

function renderBlock(block, i) {
  switch (block.type) {
    case "h1":
      return (
        <h1 key={i} className="text-3xl font-semibold mt-8 first:mt-0 mb-3 text-foreground">
          {renderInline(block.text)}
        </h1>
      );
    case "h2":
      return (
        <h2 key={i} className="text-2xl font-semibold mt-8 mb-3 text-foreground">
          {renderInline(block.text)}
        </h2>
      );
    case "h3":
      return (
        <h3 key={i} className="text-xl font-semibold mt-6 mb-2 text-foreground">
          {renderInline(block.text)}
        </h3>
      );
    case "ul":
      return (
        <ul key={i} className="space-y-1.5 mb-4 list-disc list-outside ml-5 marker:text-brand">
          {block.items.map((item, j) => (
            <li key={j} className="text-sm leading-7 text-muted-foreground">
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={i} className="space-y-1.5 mb-4 list-decimal list-outside ml-5 marker:text-brand marker:font-medium">
          {block.items.map((item, j) => (
            <li key={j} className="text-sm leading-7 text-muted-foreground pl-1">
              {renderInline(item)}
            </li>
          ))}
        </ol>
      );
    case "table":
      return (
        <div key={i} className="overflow-x-auto mb-4">
          <table className="w-full text-sm border border-border rounded-lg">
            <thead className="bg-muted/40">
              <tr>
                {block.headers.map((h, j) => (
                  <th
                    key={j}
                    className="px-3 py-2 text-left text-xs font-semibold text-foreground uppercase tracking-wider border-b border-border"
                  >
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="hover:bg-muted/20">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3 py-2 text-sm text-muted-foreground border-b border-border last:border-b-0">
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "p":
      return (
        <p key={i} className="text-sm leading-7 text-muted-foreground mb-3">
          {renderInline(block.text)}
        </p>
      );
    case "hr":
      return <hr key={i} className="my-6 border-border" />;
    default:
      return null;
  }
}

function parseMarkdown(md) {
  // Strip HTML comments (e.g. <!-- screenshot: ... -->)
  const cleaned = md.replace(/<!--[\s\S]*?-->/g, "").trim();

  const lines = cleaned.split("\n");
  const blocks = [];
  let listItems = null;
  let listType = null;
  let pBuffer = [];

  function flushList() {
    if (listItems && listItems.length > 0) {
      blocks.push({ type: listType, items: listItems });
      listItems = null;
      listType = null;
    }
  }
  function flushP() {
    if (pBuffer.length > 0) {
      blocks.push({ type: "p", text: pBuffer.join(" ") });
      pBuffer = [];
    }
  }
  function flushAll() {
    flushList();
    flushP();
  }

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trimEnd();

    if (line.startsWith("### ")) {
      flushAll();
      blocks.push({ type: "h3", text: line.slice(4) });
    } else if (line.startsWith("## ")) {
      flushAll();
      blocks.push({ type: "h2", text: line.slice(3) });
    } else if (line.startsWith("# ")) {
      flushAll();
      blocks.push({ type: "h1", text: line.slice(2) });
    } else if (line === "---") {
      flushAll();
      blocks.push({ type: "hr" });
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      flushP();
      if (listType !== "ul") {
        flushList();
        listType = "ul";
        listItems = [];
      }
      listItems.push(line.slice(2));
    } else if (/^\d+\.\s/.test(line)) {
      flushP();
      if (listType !== "ol") {
        flushList();
        listType = "ol";
        listItems = [];
      }
      listItems.push(line.replace(/^\d+\.\s/, ""));
    } else if (line.startsWith("|") && line.endsWith("|")) {
      // Table — gather contiguous table rows
      flushAll();
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i].trim());
        i++;
      }
      i--;
      if (tableLines.length >= 2) {
        const headers = parseTableRow(tableLines[0]);
        // tableLines[1] is the separator (---|---) — skip it
        const rows = tableLines.slice(2).map(parseTableRow);
        blocks.push({ type: "table", headers, rows });
      }
    } else if (line.trim() === "") {
      flushAll();
    } else {
      flushList();
      pBuffer.push(line);
    }
  }
  flushAll();
  return blocks;
}

function parseTableRow(line) {
  // Strip leading and trailing pipes, split on pipe, trim each cell
  return line
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((cell) => cell.trim());
}

// Inline parsing — bold, italic, code, links
function renderInline(text) {
  if (!text) return null;
  const parts = [];
  // Tokenize on link / bold / italic / code (link pattern first)
  const regex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[2] && match[3]) {
      // [text](url)
      const linkText = match[2];
      const href = match[3];
      const isInternal = href.startsWith("/");
      if (isInternal) {
        parts.push(
          <Link key={key++} to={href} className="text-brand hover:underline">
            {linkText}
          </Link>
        );
      } else {
        parts.push(
          <a
            key={key++}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand hover:underline"
          >
            {linkText}
          </a>
        );
      }
    } else if (match[4]) {
      parts.push(
        <strong key={key++} className="font-semibold text-foreground">
          {match[4]}
        </strong>
      );
    } else if (match[5]) {
      parts.push(<em key={key++}>{match[5]}</em>);
    } else if (match[6]) {
      parts.push(
        <code key={key++} className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
          {match[6]}
        </code>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}
